import type { Coding, Quantity } from "@medplum/fhirtypes";
import type { UnitValue } from "@repo/domain";
import { UCUM_SYSTEM } from "@repo/fhir-systems";

export { UCUM_SYSTEM };

/**
 * A measured unit, written to FHIR as both halves at once.
 *
 * `Quantity.unit` is display text and `Quantity.code` is the machine identity,
 * and FHIR keeps them separate precisely because they differ: a clinician reads
 * `in`, a converter needs `[in_i]`. Writing the display into `code` — which is
 * what a single free-text unit string forces — asserts a UCUM code that does
 * not exist, and asserting it wrongly is worse than omitting the system.
 */
export function unitToQuantity(unit: UnitValue | undefined): Pick<Quantity, "unit" | "system" | "code"> {
  if (!unit) return {};
  return { unit: unit.display, system: UCUM_SYSTEM, code: unit.code };
}

/**
 * Read a unit back. A quantity carrying only display text predates the coded
 * unit and cannot be repaired here — there is no code to derive from a display
 * — so it reads as unitless rather than as a unit nothing will ever match.
 */
export function unitFromQuantity(quantity: Quantity | undefined): UnitValue | undefined {
  if (!quantity?.code) return undefined;
  return { code: quantity.code, display: quantity.unit ?? quantity.code };
}

/** The same read, where a declaration stores its unit as a `Coding` rather than a `Quantity`. */
export function unitFromCoding(coding: Coding | undefined): UnitValue | undefined {
  if (!coding?.code) return undefined;
  return { code: coding.code, display: coding.display ?? coding.code };
}
